import React, { useState } from 'react';
import './App.css';

function App() {
  const [searchType, setSearchType] = useState(''); // No selection by default
  const [pincode, setPincode] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [images, setImages] = useState({}); // Stores images for each mode
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null); // For modal display

  /*
    Legend data and colors
  */
  const freezeLegend = [
    '> 20°F / -6.7°C',
    '15°F / -9.4°C',
    '10°F / -12.2°C',
    '5°F / -15°C',
    '0°F / -17.8°C',
    '-5°F / -20.6°C',
    '-10°F / -23.3°C',
    '-15°F / -26.1°C',
    '-20°F / -28.9°C',
    '-25°F / -31.7°C',
    '-30°F / -34.4°C',
    '-35°F / -37.2°C',
    '-40°F / -40°C',
    '-45°F / -42.8°C',
    '-50°F / -45.6°C',
    '-55°F / -48.3°C',
    '-60°F / -51.1°C',
    '-65°F / -53.9°C',
    '-70°F / -56.7°C',
    '-75°F / -59.4°C',
    '≤ -80°F / -62.2°C',
  ];
  const freezeColors = [
    '#FDE5E5', '#FBCBCD', '#F8B1B6', '#F6979E', '#F37D86',
    '#F0636E', '#ED4956', '#EA2F3E', '#E71526', '#CF1322',
    '#B7111E', '#9F0E1A', '#870C16', '#6F0A12', '#57080E',
    '#3F050A', '#270306', '#110102', '#000000', '#000000',
    '#000000',
  ];

  const floodLegend = ['High', 'Moderate'];
  const floodColors = ['#FF00FF', '#FFFF00']; // Magenta, Yellow

  const earthquakeLegend = ['50', '100', '250', '500', '>500', 'No Data'];
  const earthquakeColors = ['#0000FF', '#FF0000', '#FFA500', '#00FF00', '#FFFFFF', 'PATTERN'];

  // Helper to render legend items with color swatches for the selected mode
  const renderLegend = (mode) => {
    let items = [];
    let colors = [];
    if (mode === 'freeze') {
      items = freezeLegend;
      colors = freezeColors;
    } else if (mode === 'flood') {
      items = floodLegend;
      colors = floodColors;
    } else if (mode === 'earthquake') {
      items = earthquakeLegend;
      colors = earthquakeColors;
    }

    return (
      <div className="legend-container">
        {items.map((item, index) => {
          let colorStyle = { backgroundColor: colors[index] };

          if (mode === 'earthquake') {
            if (item === 'No Data') {
              // We'll apply a special class for hatching
              colorStyle = {};
            } else if (item === '>500') {
              colorStyle = {
                backgroundColor: '#FFFFFF',
                border: '1px solid #000',
              };
            } else if (colors[index] === 'PATTERN') {
              // fallback if needed
              colorStyle = {};
            }
          }

          return (
            <div key={item} className="legend-item">
              <span
                className={
                  `legend-color ${
                    (mode === 'earthquake' && item === 'No Data') ? 'no-data' : ''
                  }`
                }
                style={colorStyle}
              />
              <span className="legend-text">{item}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Handle "Locate Me" button click to fetch current position
  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLat(position.coords.latitude.toFixed(6));
        setLon(position.coords.longitude.toFixed(6));
        setError(null);
      },
      () => {
        setError('Unable to retrieve your location.');
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setImages({});

    // Validate lat/lon if Latitude and Longitude is selected
    if (searchType === 'latlong') {
      const latNum = parseFloat(lat);
      const lonNum = parseFloat(lon);
      if (isNaN(latNum) || latNum < -90 || latNum > 90) {
        setError('Latitude must be between -90 and 90.');
        setLoading(false);
        return;
      }
      if (isNaN(lonNum) || lonNum < -180 || lonNum > 180) {
        setError('Longitude must be between -180 and 180.');
        setLoading(false);
        return;
      }
    }

    const modes = ['earthquake', 'freeze', 'flood'];

    // Create an array of promises—one for each mode
    const fetchCalls = modes.map((mode) => {
      const payload =
        searchType === 'pincode'
          ? { pincode, zoom: 15, radius: 1, mode }
          : { lat: parseFloat(lat), lon: parseFloat(lon), zoom: 15, radius: 1, mode };

      return fetch('https://annotation.wimwisure.com/Maps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`API request failed for mode: ${mode}`);
          }
          return response.blob();
        })
        .then((blob) => {
          return { mode, url: URL.createObjectURL(blob) };
        });
    });

    try {
      const results = await Promise.all(fetchCalls);
      // Map results by mode
      const imagesMap = {};
      results.forEach(({ mode, url }) => {
        imagesMap[mode] = url;
      });
      setImages(imagesMap);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Full-screen loader overlay */}
      {loading && (
        <div className="fullscreen-loader">
          <div className="loader-content">
            <div className="spinner"></div>
            <p>Loading GeoSpatial Data.. Please wait</p>
          </div>
        </div>
      )}

      <div className="App">
        {/* Company logo */}
        <div className="logo-container">
          <img
            src="https://survezy.s3.ap-south-1.amazonaws.com/logos/wimwisure.png"
            alt="Wimwisure Logo"
            className="logo"
          />
        </div>

        <h1>NATCAT Data</h1>

        <form onSubmit={handleSubmit}>
          {/* Radio buttons to select search type */}
          <div className="input-group">
            <fieldset className="search-type-fieldset">
              <legend className="search-type-legend">Select Search Type</legend>
              <label>
                <input
                  type="radio"
                  name="searchType"
                  value="pincode"
                  checked={searchType === 'pincode'}
                  onChange={(e) => setSearchType(e.target.value)}
                />
                PIN Code
              </label>
              <label>
                <input
                  type="radio"
                  name="searchType"
                  value="latlong"
                  checked={searchType === 'latlong'}
                  onChange={(e) => setSearchType(e.target.value)}
                />
                Latitude and Longitude
              </label>
            </fieldset>
          </div>

          {/* Show input field only after a search type is selected */}
          {searchType === 'pincode' && (
            <div className="input-group">
              <label htmlFor="pincode">Pincode:</label>
              <input
                id="pincode"
                type="text"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                placeholder="Enter PIN Code"
                required
              />
            </div>
          )}

          {searchType === 'latlong' && (
            <>
              <div className="input-group">
                <label htmlFor="lat">Latitude:</label>
                <input
                  id="lat"
                  type="number"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  placeholder="Enter Latitude"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="lon">Longitude:</label>
                <input
                  id="lon"
                  type="number"
                  value={lon}
                  onChange={(e) => setLon(e.target.value)}
                  placeholder="Enter Longitude"
                  required
                />
              </div>
              <button type="button" onClick={handleLocateMe}>
                Locate Me
              </button>
            </>
          )}

          {/* Show the submit button only when a search type is selected */}
          {searchType && (
            <button type="submit" className="submit-btn">
              Enter
            </button>
          )}
        </form>

        {error && <p className="error">Error: {error}</p>}

        {Object.keys(images).length > 0 && (
          <div className="results">
            {['earthquake', 'freeze', 'flood'].map((mode) => (
              <div
                className="result"
                key={mode}
                onClick={() => setSelectedImage({ mode, url: images[mode] })}
              >
                <div className="result-img-container">
                  <img src={images[mode]} alt={`${mode} result`} />
                  <div className="overlay">
                    <span className="icon">🔍</span>
                    <span className="overlay-text">Click to view</span>
                  </div>
                </div>
                <h2>{mode.charAt(0).toUpperCase() + mode.slice(1)}</h2>
              </div>
            ))}
          </div>
        )}

        {/* Modal for enlarged image and legend */}
        {selectedImage && (
          <div className="modal" onClick={() => setSelectedImage(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <span className="close" onClick={() => setSelectedImage(null)}>
                &times;
              </span>
              <img
                src={selectedImage.url}
                alt={`${selectedImage.mode} enlarged`}
                className="modal-image"
              />
              <h2>{selectedImage.mode.charAt(0).toUpperCase() + selectedImage.mode.slice(1)}</h2>

              <div className="legend">
                <h3>Legend</h3>
                {renderLegend(selectedImage.mode)}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default App;
